import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../../api/axios';
import { ArrowLeft, Edit, Receipt, Printer, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import { useOrganization } from '../../hooks/useOrganization';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDate } from '../../utils/dateFormat';

const QuotationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { hasPermission } = usePermissions();
  const [quotation, setQuotation] = useState(null);
  const [settings, setSettings] = useState(null);
  const [hasInvoice, setHasInvoice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [printNotes, setPrintNotes] = useState(false);
  const [statusChangeModal, setStatusChangeModal] = useState({ show: false, newStatus: '' });

  useEffect(() => {
    if (id && id !== 'undefined') {
      fetchQuotation();
      fetchSettings();
    }
  }, [id]);

  useEffect(() => {
    if (quotation) {
      document.title = `Quotation-${quotation.quotationNumber || `QUO-${quotation.id}`}`;
    }
    return () => {
      document.title = 'AGMS - Auto Garage Management System';
    };
  }, [quotation]);

  const fetchQuotation = async () => {
    try {
      const [quotationRes, invoicesRes] = await Promise.all([
        axios.get(`/api/quotations/${id}`),
        hasPermission('invoices.read') ? axios.get('/api/invoices') : Promise.resolve({ data: { data: [] } })
      ]);
      
      setQuotation(quotationRes.data);
      
      // Check if invoice exists for this quotation
      const invoicesData = invoicesRes.data?.data || [];
      const invoice = invoicesData.find(inv => inv.quotationId === parseInt(id));
      setHasInvoice(!!invoice);
    } catch (error) {
      toast.error('Failed to load quotation');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/settings/config');
      setSettings(response.data);
    } catch (error) {
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!quotation) return <div className="p-6">Quotation not found</div>;

  const calculateTotals = () => {
    // Use stored values from quotation instead of recalculating
    const subtotal = parseFloat(quotation.subtotal) || 0;
    const discount = parseFloat(quotation.discount) || 0;
    const tax = parseFloat(quotation.tax) || 0;
    const total = parseFloat(quotation.total) || 0;
    
    // Calculate VAT percentage from stored values
    const vatPercentage = tax > 0 && subtotal > discount
      ? Math.round((tax / (subtotal - discount)) * 100 * 100) / 100
      : 0;
    
    return { subtotal, discount, tax, total, vatPercentage };
  };

  const { subtotal, discount, tax, total, vatPercentage } = calculateTotals();

  const itemCount = (quotation.items || []).length;
  const getFontScale = () => {
    if (itemCount <= 2) return 1.15;
    if (itemCount <= 4) return 1.05;
    if (itemCount <= 6) return 0.98;
    if (itemCount <= 8) return 0.92;
    if (itemCount <= 10) return 0.88;
    if (itemCount <= 12) return 0.84;
    if (itemCount <= 14) return 0.8;
    if (itemCount <= 16) return 0.76;
    if (itemCount <= 18) return 0.72;
    if (itemCount <= 20) return 0.68;
    if (itemCount <= 22) return 0.64;
    if (itemCount <= 25) return 0.6;
    if (itemCount <= 28) return 0.56;
    if (itemCount <= 30) return 0.52;
    return 0.48;
  };
  const fontScale = getFontScale();

  const handlePrint = () => {
    window.print();
  };

  const handleStatusChange = () => {
    setStatusChangeModal({ show: true, newStatus: 'Sent' });
  };

  const confirmStatusChange = async () => {
    try {
      await axios.patch(`/api/quotations/${id}/status`, {
        status: statusChangeModal.newStatus
      });
      setStatusChangeModal({ show: false, newStatus: '' });
      fetchQuotation();
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };



  return (
    <>
      <style jsx>{`
        @media print {
          .dynamic-scale {
            --font-scale: ${fontScale};
          }
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { 
            position: absolute; left: 0; top: 0; width: 100%;
          }
          .no-print { display: none !important; }
          @page { 
            size: A4;
            margin: 6mm 10mm 10mm 10mm;
          }
          .print-header { 
            margin-bottom: 4px;
            padding-bottom: 4px;
            page-break-inside: avoid;
          }
          .logo-container {
            text-align: center;
            margin-bottom: 8pt;
            padding: 4pt 0;
            border-bottom: 1pt solid #ddd;
          }
          .invoice-header-logo {
            height: 1.3in;
            width: 100%;
            object-fit: fill;
            display: block;
            margin: 0 auto;
          }
          .print-logo { 
            height: calc(55px * var(--font-scale, 1));
            width: calc(55px * var(--font-scale, 1));
            object-fit: contain;
            border-radius: 6px !important;
          }
          .print-section { 
            margin-bottom: 2px;
            padding: 0px;
            line-height: 1.1;
          }
          .print-section h1 {
            font-size: calc(16px * var(--font-scale, 1)) !important;
            margin: 0 0 1px 0 !important;
            font-weight: bold;
          }
          .print-section h2 {
            font-size: calc(12px * var(--font-scale, 1)) !important;
            margin: 0 !important;
            font-weight: bold;
          }
          .print-section p {
            font-size: calc(9px * var(--font-scale, 1)) !important;
            margin: 0 !important;
            line-height: 1.3;
          }
          .print-grid { 
            grid-template-columns: 1fr 1fr 1fr !important;
            gap: 6px !important;
            margin-top: 4px !important;
            margin-bottom: 6px !important;
            padding: 2px 0;
          }
          .print-details { line-height: 1.2; }
          .print-details > div { 
            margin: 0 0 2px 0;
            font-size: calc(10px * var(--font-scale, 1));
            line-height: 1.4;
          }
          .print-table { 
            width: 100%; 
            border-collapse: collapse;
            margin: 4px 0 !important;
          }
          .print-table thead {
            display: table-header-group;
          }
          .print-table tbody {
            display: table-row-group;
          }
          .print-table tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          .print-table th {
            border: 1px solid #555 !important;
            padding: calc(4px * var(--font-scale, 1)) calc(5px * var(--font-scale, 1)) !important;
            font-size: calc(9px * var(--font-scale, 1)) !important;
            line-height: 1.3;
            background-color: #f0f0f0 !important;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            color: #333;
          }
          .print-table td { 
            border: 1px solid #999 !important;
            padding: calc(4px * var(--font-scale, 1)) calc(5px * var(--font-scale, 1)) !important;
            font-size: calc(10px * var(--font-scale, 1)) !important;
            line-height: 1.3;
          }
          .print-table .text-center { text-align: center; }
          .print-totals { 
            margin-top: calc(16px * var(--font-scale, 1)) !important;
            display: flex;
            justify-content: flex-end;
            align-items: flex-start;
            page-break-inside: avoid;
          }
          .print-totals > div:first-child {
            display: none;
          }
          .print-totals .w-64 {
            font-size: calc(12px * var(--font-scale, 1)) !important;
            line-height: 1.4;
            padding: calc(8px * var(--font-scale, 1));
            border: 1px solid #ddd;
            border-radius: 4px;
            min-width: 220px;
          }
          .print-totals .w-64 > div {
            padding: calc(2px * var(--font-scale, 1)) 0;
          }
          .print-terms { 
            font-size: 7px !important;
            line-height: 1.3;
            padding: 2px 0;
            margin-top: 4px;
          }
          .print-terms p { margin: 1px 0; font-size: 7px !important; }
          .print-notes {
            font-size: 7px !important;
            line-height: 1.3;
            padding: 2px 0;
            margin-top: 4px;
            page-break-inside: avoid;
          }
          .print-notes h3 {
            font-size: 8px !important;
            margin-bottom: 2px !important;
          }
          .print-notes p {
            font-size: 7px !important;
            margin: 0;
            line-height: 1.3;
            background: none !important;
            border: none !important;
            padding: 0 !important;
          }
          .print-hide-status { display: none; }
          .print-hide-customer { display: none; }
          .print-signature { 
            margin-top: auto;
            padding-top: 20px;
            padding-bottom: 5px;
            page-break-inside: avoid;
            position: relative;
          }
          .print-signature p {
            font-size: 11px !important;
            margin-top: 3px;
            font-weight: 500;
          }
          .page-footer {
            position: fixed;
            bottom: 3mm;
            left: 10mm;
            right: 10mm;
            text-align: center;
            font-size: 8px;
            color: #555;
            border-top: 1px solid #ccc;
            padding-top: 3px;
            display: none;
          }
          @media print {
            .page-footer {
              display: block;
            }
          }
          h3 {
            font-size: calc(10px * var(--font-scale, 1)) !important;
            margin-bottom: 2px !important;
            margin-top: 4px !important;
            font-weight: bold;
          }
          .print-grid h3 {
            font-size: calc(10px * var(--font-scale, 1)) !important;
          }
          .print-area > div { padding: 0 !important; }
          .print-area > div > div { margin: 0 !important; }
          .space-y-6 > * + * { margin-top: 4px !important; }
          .print:shadow-lg { box-shadow: none !important; }
        }
      `}</style>
    <div className="p-6 print-area dynamic-scale">
      {/* Header with Logo - PRINT ONLY */}
      <div className="print-header hidden print:block">
        {/* Full-width Logo Header */}
        {(organization?.invoiceLogoUrl || organization?.logoUrl) && (
          <div className="logo-container mb-4" style={{ textAlign: 'center', borderBottom: '1pt solid #ddd', paddingBottom: '4pt' }}>
            <img 
              src={organization?.invoiceLogoUrl || organization?.logoUrl} 
              alt="Company Logo" 
              className="invoice-header-logo"
              style={{ height: '1.3in', width: '100%', objectFit: 'fill', display: 'block', margin: '0 auto' }}
            />
          </div>
        )}
        <div className="flex justify-between items-center print-section">
          <div>
            <h1 className="text-2xl font-bold">{organization?.companyName || 'Al Makhmari Garage'}</h1>
            {organization?.vatId && (
              <p className="text-sm font-medium">TRN: {organization.vatId}</p>
            )}
            <p className="text-sm">{organization?.address}</p>
            <p className="text-sm">{organization?.email}</p>
            <p className="text-sm">Phone: {organization?.companyContact}</p>
          </div>
          <div className="text-right">
            <h2 className="text-xl font-bold">{settings?.quotationHeading || 'QUOTATION'}</h2>
            <p>#{quotation.quotationNumber || `QUO-${quotation.id}`}</p>
            <p>Date: {formatDate(quotation.quotationDate)}</p>
          </div>
        </div>
      </div>

      {/* Action Buttons - screen only */}
      <div className="flex flex-wrap gap-2 mb-4 no-print">
        <button onClick={() => navigate('/accounts')} className="flex items-center text-gray-600 hover:text-gray-900 text-sm border px-3 py-2 rounded-md">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>
        <button onClick={handlePrint} className="bg-gray-600 text-white px-3 py-2 rounded-md hover:bg-gray-700 flex items-center gap-1 text-sm">
          <Printer className="w-4 h-4" /> Print
        </button>
        {!hasInvoice && quotation.status !== 'Accepted' && (
          <Link to={`/accounts/quotations/${quotation.id}/edit`} className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 flex items-center gap-1 text-sm">
            <Edit className="w-4 h-4" /> Edit
          </Link>
        )}
        {quotation.status === 'Draft' && (
          <button onClick={handleStatusChange} className="bg-orange-600 text-white px-3 py-2 rounded-md hover:bg-orange-700 flex items-center gap-1 text-sm">
            <RefreshCw className="w-4 h-4" /> Status
          </button>
        )}
        {quotation.status === 'Accepted' && !hasInvoice && hasPermission('invoices.create') && (
          <Link to={`/accounts/invoices/create?quotationId=${quotation.id}`} className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 flex items-center gap-1 text-sm">
            <Receipt className="w-4 h-4" /> Invoice
          </Link>
        )}
      </div>

      {/* Quotation Title - screen only */}
      <h1 className="text-xl font-bold mb-4 no-print">Quotation #{quotation.quotationNumber || `QUO-${quotation.id}`}</h1>

      {/* 3-Column Details - always visible */}
      <div className="grid grid-cols-3 gap-4 print-grid items-start">
        <div>
          <h3 className="text-lg font-semibold mb-2">Attention To:</h3>
          <div className="space-y-2 text-sm print-details">
            <div>{quotation.customer?.name}</div>
            <div>{quotation.customer?.contact || quotation.customer?.phone || 'Contact not available'}</div>
            {quotation.customer?.email && <div>{quotation.customer.email}</div>}
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-2">Quotation Details</h3>
          <div className="space-y-2 text-sm print-details">
            <div><span className="font-medium">Branch:</span> {quotation.branch?.name}</div>
            <div><span className="font-medium">Date:</span> {formatDate(quotation.quotationDate)}</div>
            <div><span className="font-medium">Expiry:</span> {formatDate(quotation.expiryDate)}</div>
            <div><span className="font-medium">Status:</span> {quotation.status}</div>
          </div>
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-2">Vehicle Details</h3>
          <div className="space-y-2 text-sm print-details">
            <div><span className="font-medium">Vehicle:</span> {quotation.vehicleNumber || 'N/A'}</div>
            <div><span className="font-medium">VIN:</span> {quotation.vinNumber || 'N/A'}</div>
            <div><span className="font-medium">Odometer:</span> {quotation.odometerReading || 'N/A'}</div>
          </div>
        </div>
      </div>

      <div className="print:shadow-none print:border-0 mt-4">
        <div className="print:mt-0">
          <h3 className="text-lg font-semibold mb-1 print:mb-0">Items</h3>
          <div className="print:mt-0">
            <table className="min-w-full divide-y divide-gray-200 print-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">SN</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(quotation.items || []).map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 text-sm text-center">{index + 1}</td>
                    <td className="px-6 py-4 text-sm">{item.description}</td>
                    <td className="px-6 py-4 text-sm text-center">{item.quantity}</td>
                    <td className="px-6 py-4 text-sm text-center">{formatCurrency(parseFloat(item.price) || 0, organization?.currency_code)}</td>
                    <td className="px-6 py-4 text-sm text-center">{formatCurrency(parseFloat(item.total) || 0, organization?.currency_code)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-between print-totals">
          <div className="w-1/2 pr-4">
          </div>
          <div className="w-64 space-y-2 text-sm ml-auto">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>{formatCurrency(subtotal, organization?.currency_code)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between">
                <span>Discount:</span>
                <span>{formatCurrency(discount, organization?.currency_code)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Tax ({Number(vatPercentage).toFixed(0)}%):</span>
              <span>{formatCurrency(tax, organization?.currency_code)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t pt-2">
              <span>Total:</span>
              <span>{formatCurrency(total, organization?.currency_code)}</span>
            </div>
          </div>
        </div>

        {/* Notes Section - printable only if checkbox is checked */}
        {(() => {
          const rawNotes = quotation.notes || '';
          const lines = rawNotes.split('\n');
          const userNotes = lines.filter(line => 
            !line.match(/^\[CreatedBy:/) && 
            !line.match(/^\[.*\] Edited by/) &&
            !line.match(/^\[.*\] Updated by/) &&
            !line.match(/^\[.*\] Status changed/) &&
            !line.match(/^\[.*\] Job card/) &&
            !line.match(/^\[.*\] Quotation created by/) &&
            !line.match(/^\[.*\] Created by/)
          ).join('\n').trim().replace(/&#x2F;/g, '/').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#x27;/g, "'").replace(/&quot;/g, '"');
          
          const historyEntries = lines.filter(line => 
            line.match(/^\[.*\] Edited by/) ||
            line.match(/^\[.*\] Updated by/) ||
            line.match(/^\[.*\] Status changed/) ||
            line.match(/^\[.*\] Job card/) ||
            line.match(/^\[.*\] Quotation created by/) ||
            line.match(/^\[.*\] Created by/)
          );

          return (
            <>
              {userNotes && (
                <div className={printNotes ? 'print-notes' : 'no-print'}>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">Notes</h3>
                    <label className="flex items-center gap-2 text-sm text-gray-500 no-print cursor-pointer">
                      <input
                        type="checkbox"
                        checked={printNotes}
                        onChange={(e) => setPrintNotes(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600"
                      />
                      Include in print
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 whitespace-pre-line bg-gray-50 p-3 rounded-lg border">{userNotes}</p>
                </div>
              )}

              {/* Terms & Conditions - always prints */}
              <div className="mt-4">
                <h3 className="text-lg font-semibold mb-2">Terms & Conditions</h3>
                <div className="text-sm text-gray-600 print-terms">
                  {quotation.termsConditions ? (
                    <div className="whitespace-pre-line">{quotation.termsConditions}</div>
                  ) : (
                    <div>
                      {(quotation.status === 'Draft' || quotation.status === 'Pending') && (
                        <p className="font-medium mb-1">Please review the details and confirm to proceed.</p>
                      )}
                      <p><strong>Validity</strong> – This quotation is valid for expiry date mentioned in Quotation Details above.</p>
                      <p><strong>Pricing</strong> – Prices are subject to change if parts, labor rates, or taxes vary before approval.</p>
                      <p><strong>Approval</strong> – Work will commence only after written approval of this quotation.</p>
                      <p><strong>Payment</strong> – Payment terms as per agreement (e.g., 50% advance, balance upon completion).</p>
                      <p><strong>Parts Availability</strong> – Quoted parts are subject to availability at the time of approval.</p>
                      <p><strong>Warranty</strong> – Warranty applies only to parts and labor specified in the approved job card.</p>
                      <p><strong>Exclusions</strong> – Additional repairs or parts not listed will be charged separately.</p>
                      <p><strong>Liability</strong> – The company is not liable for delays caused by parts unavailability or unforeseen issues.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* History - never prints, non-editable audit trail */}
              {historyEntries.length > 0 && (
                <div className="no-print mt-6">
                  <h3 className="text-lg font-semibold mb-2 text-gray-700">History</h3>
                  <div className="text-xs text-gray-500 space-y-1 bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {historyEntries.reverse().map((entry, idx) => (
                      <div key={idx} className="flex items-center gap-2 py-0.5">
                        <span className="text-gray-400">•</span>
                        <span>{entry.replace(/&#x2F;/g, '/').replace(/&amp;/g, '&')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}
        
        {/* Customer Signature - Print Only */}
        <div className="hidden print:block print-signature">
          <div className="flex justify-between" style={{ marginTop: '40px' }}>
            <div className="border-t border-black pt-2 w-48">
              <p className="text-center" style={{ fontSize: '11px' }}>Authorized Signature</p>
            </div>
            <div className="border-t border-black pt-2 w-48">
              <p className="text-center" style={{ fontSize: '11px' }}>Customer Signature & Date</p>
            </div>
          </div>
        </div>

        {/* Footer - Print Only */}
        <div className="page-footer">
          {organization?.companyName && <span>{organization.companyName}</span>}
          {organization?.companyContact && <span> | {organization.companyContact}</span>}
          {organization?.email && <span> | {organization.email}</span>}
          {organization?.address && <span> | {organization.address}</span>}
          {organization?.city && <span>, {organization.city}</span>}
          {organization?.country && <span>, {organization.country}</span>}
        </div>
      </div>

      {/* Status Change Modal */}
      {statusChangeModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Change Quotation Status</h3>
            <p className="text-gray-600 mb-4">
              Change status for quotation #{quotation.quotationNumber || `QUO-${quotation.id}`}
            </p>
            <select
              value={statusChangeModal.newStatus}
              onChange={(e) => setStatusChangeModal(prev => ({ ...prev, newStatus: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
            >
              <option value="Sent">Sent</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
              <option value="Expired">Expired</option>
            </select>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setStatusChangeModal({ show: false, newStatus: '' })}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default QuotationView;
